import { readFileSync } from 'fs'
import { parse } from 'yaml'
import { z } from 'zod'

export const TenantConfigSchema = z.object({
  tenant: z.object({
    name: z.string().min(1),
    slug: z.string().min(1).regex(/^[a-z0-9-]+$/, 'slug must be lowercase alphanumeric with hyphens'),
  }),
  access: z.object({
    initial_expert_invites: z.array(z.string().email()).default([]),
    initial_dual_access_invites: z.array(z.string().email()).default([]),
    initial_line_manager_invites: z.array(z.string().email()).default([]),
  }),
  ui: z.object({
    submission_prompt_text: z.string().min(1),
    additional_info_prompt_text: z.string().min(1),
    submission_max_chars: z.number().int().min(100).max(10000).default(4000),
    post_submission_waiting_body: z.string().default(
      "We're checking whether we need any additional information. This usually takes a few seconds — please wait.",
    ),
    post_submission_needs_info_body: z.string().default(
      'To help our expert respond as quickly as possible, please answer the questions below.',
    ),
    post_submission_complete_body: z.string().default(
      "Your issue is now in the queue and an expert will review it shortly. You'll receive an email when they respond.",
    ),
  }),
  ai: z.object({
    provider: z.enum(['anthropic', 'openai', 'bedrock', 'vertex', 'ollama']).default('anthropic'),
    model: z.string().min(1),
    temperature: z.number().min(0).max(1).default(0.3),
    min_info_score_threshold: z.number().min(0).max(1).default(0.7),
    max_clarification_cycles: z.number().int().min(0).default(2),
    ai_job_timeout_seconds: z.number().int().min(1).default(120),
    data_processing_consent: z.boolean().default(true),
    embedding_model: z.string().optional(),
  }),
  limits: z.object({
    max_attachment_bytes: z.number().int().positive().default(26214400),
    max_ticket_attachment_bytes: z.number().int().positive().default(104857600),
    max_messages_loaded_for_ai: z.number().int().min(1).default(50),
  }).default({}),
  notifications: z.object({
    email: z.boolean().default(true),
    slack: z.boolean().default(false),
    whatsapp: z.boolean().default(false),
    include_response_in_email: z.boolean().default(true),
    expert_digest_enabled: z.boolean().default(true),
  }).default({}),
  intake: z.object({
    departments: z.array(z.string()).default(['Quality', 'Operations', 'Engineering', 'Regulatory']),
    issue_categories: z.array(z.string()).default(['Validation', 'Equipment', 'Compliance', 'Deviation', 'Other']),
    affected_systems: z.array(z.string()).default(['Sealing', 'Testing', 'Assembly', 'Packaging', 'Other']),
    action_requested_options: z.array(z.string()).default([
      'Approval of proposed strategy',
      'Guidance on next steps',
      'Regulatory interpretation',
      'Other',
    ]),
    resolution_severity_levels: z.array(z.string()).default(['Critical', 'Major', 'Minor', 'Observation']),
    resolution_concern_types: z.array(z.string()).default([
      'Process gap',
      'Knowledge gap',
      'Confusion / unclear guidance',
      'Other',
    ]),
    category_abbreviations: z.record(z.string(), z.string()).default({
      Validation: 'VAL',
      Equipment: 'EQP',
      Compliance: 'CMP',
      Deviation: 'DEV',
      Other: 'OTH',
    }),
  }).default({}),
})

export type TenantConfig = z.infer<typeof TenantConfigSchema>

const configCache = new Map<string, TenantConfig>()

export function _resetConfigCacheForTests() {
  configCache.clear()
}

/**
 * Reads and validates a tenant config YAML file.
 * Result is cached per path for the lifetime of the serverless container.
 */
export function loadTenantConfig(filePath: string): TenantConfig {
  const cached = configCache.get(filePath)
  if (cached) return cached
  const raw = readFileSync(filePath, 'utf-8')
  const parsed = parse(raw)
  const config = TenantConfigSchema.parse(parsed)
  configCache.set(filePath, config)
  return config
}
